import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import {
  batchDetailQueryOptions,
  isOwnBatch,
  useBatchLiveProgress,
} from '../../../api/cards';

export const Route = createFileRoute('/_auth/history/$batchId')({
  component: BatchDetailPage,
});

function BatchDetailPage() {
  const { batchId } = Route.useParams();
  const { data: batch } = useQuery({
    ...batchDetailQueryOptions(batchId),
    // Someone else's running batch has no stream to follow (owner only):
    // its progress is these stats, so keep them fresh until it finishes.
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.status === 'processing' && !isOwnBatch(data) ? 4_000 : false;
    },
  });
  const { data: progress, status } = useBatchLiveProgress(batch);
  const { t } = useTranslation();

  const pct =
    progress && progress.total > 0
      ? Math.round((progress.done / progress.total) * 100)
      : 0;

  const isDone =
    status === 'done' ||
    (progress !== undefined &&
      progress.total > 0 &&
      progress.done >= progress.total);

  return (
    <div>
      <Link to="/history" className="btn-link mb-6">
        ← {t('nav.history')}
      </Link>

      <section className="epi-card tone-blue mt-4">
        <div className="epi-card-head">
          <span className="num">B</span>
          <span className="title">
            {t('history.detailTitle', {
              id: batchId.slice(0, 8).toUpperCase(),
            })}
          </span>
          <span className="meta">
            {isDone
              ? t('history.done')
              : status === 'failed'
                ? t('history.failed')
                : t('history.running')}
          </span>
        </div>
        {progress ? (
          <div>
            <div className="mb-3 flex items-baseline justify-between">
              <p className="display text-3xl">
                {progress.done}
                <span className="text-muted"> / {progress.total}</span>
              </p>
              <p className="label">{pct}%</p>
            </div>
            <div className="h-2 w-full bg-ink/8">
              <div
                className="h-2 bg-blue transition-[width]"
                style={{ width: `${pct}%` }}
              />
            </div>
            {status === 'retrying' && !isDone && (
              <p className="mt-4 text-sm text-coral">
                {t(
                  'batchProgress.retrying',
                  'Connexion perdue — tentative de reconnexion…',
                )}
              </p>
            )}
            {status === 'failed' && !isDone && (
              <p className="mt-4 text-sm text-magenta">
                {t(
                  'batchProgress.failed',
                  'Connexion au flux de progression perdue. Rafraîchissez la page pour réessayer.',
                )}
              </p>
            )}
            {isDone && (
              <p className="display mt-4 text-lg">
                <span className="inline-block bg-green px-2 py-0.5 text-ink">
                  {t('history.done')}
                </span>
              </p>
            )}
          </div>
        ) : (
          <p className="label cursor">{t('common.loading')}</p>
        )}
      </section>
    </div>
  );
}
